import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Save } from "lucide-react";

function Toggle({ label, desc, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-[#2A2E39] bg-[#0B0C0E] p-4">
      <div><div className="text-sm font-medium text-slate-100">{label}</div><div className="text-xs text-[#94A3B8]">{desc}</div></div>
      <button data-testid={testId} onClick={() => onChange(!checked)}
        className={`relative h-6 w-11 rounded-full transition-colors ${checked ? "bg-[#D4AF37]" : "bg-[#2A2E39]"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
      </button>
    </div>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);

  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)); }, []);
  const upd = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    try { const { data } = await api.put("/admin/settings", s); setS(data); toast.success("Settings saved"); }
    catch { toast.error("Could not save"); }
  };

  if (!s) return <div className="py-20 text-center text-[#64748B]">Loading…</div>;

  const num = (label, k, desc) => (
    <div className="rounded-xl border border-[#2A2E39] bg-[#0B0C0E] p-4">
      <label className="text-sm font-medium text-slate-100">{label}</label>
      <div className="text-xs text-[#94A3B8]">{desc}</div>
      <input data-testid={`setting-${k}`} type="number" value={s[k]} onChange={(e) => upd(k, parseInt(e.target.value) || 0)}
        className="mt-2 w-32 rounded-lg border border-[#2A2E39] bg-[#1A1D24] px-3 py-2 text-sm text-slate-100 focus:border-[#D4AF37]/60 focus:outline-none" />
    </div>
  );

  return (
    <div className="max-w-3xl">
      <h1 className="font-serif-display text-3xl font-bold text-slate-50">Settings</h1>
      <p className="mt-1 text-sm text-[#94A3B8]">Stock bands, freshness policy and pricing behaviour.</p>

      <div className="mt-8 space-y-4">
        {num("Stock stale after (minutes)", "stock_stale_minutes", "Data older than this is flagged STALE.")}
        {num("Low stock threshold", "low_stock_threshold", "Available at or below this shows LOW STOCK.")}
        {num("Default safety stock", "default_safety_stock", "Buffer held back from available-to-order.")}
        {num("Max Sage retries", "max_sage_retries", "Attempts before marking REQUIRES ATTENTION.")}

        <Toggle testId="setting-show-exact-stock" label="Show exact stock to trade customers" desc="Off = customer-friendly bands only (recommended)."
          checked={s.show_exact_stock} onChange={(v) => upd("show_exact_stock", v)} />
        <Toggle testId="setting-retailer-pricing" label="Retailer-specific pricing" desc="Apply tier & promotional pricing per account."
          checked={s.retailer_specific_pricing_enabled} onChange={(v) => upd("retailer_specific_pricing_enabled", v)} />

        <div className="rounded-xl border border-[#2A2E39] bg-[#0B0C0E] p-4">
          <div className="text-sm font-medium text-slate-100">Stale-stock checkout policy</div>
          <div className="text-xs text-[#94A3B8]">What to do if Sage can't be reached at checkout.</div>
          <div className="mt-3 flex gap-2">
            {[["OPTION_A", "Block checkout (safest)"], ["OPTION_B", "Accept as pending confirmation"]].map(([v, l]) => (
              <button key={v} data-testid={`policy-${v}`} onClick={() => upd("stale_policy", v)}
                className={`rounded-full border px-4 py-2 text-sm ${s.stale_policy === v ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#2A2E39] text-slate-300"}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>

      <button data-testid="settings-save-btn" onClick={save} className="mc-btn-gold mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3"><Save className="h-4 w-4" /> Save Settings</button>
    </div>
  );
}
